import { useState, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import {
  Moon, Sun, Calendar, MapPin, Clock, Eye,
  ChevronRight, ExternalLink, AlertTriangle
} from 'lucide-react';
import { useI18n } from '../context/I18nContext';

// Eclipse data for 2024-2030
const ECLIPSES = {
  solar: [
    {
      date: '2024-04-08',
      type: 'Total',
      path: 'Mexico, USA, Canada',
      maxDuration: '4m28s',
      saros: 139,
      visibility: { lat: [15, 50], lng: [-130, -60] }
    },
    {
      date: '2024-10-02',
      type: 'Annular',
      path: 'Pacific, Chile, Argentina',
      maxDuration: '7m25s',
      saros: 144,
      visibility: { lat: [-60, 10], lng: [-130, -30] }
    },
    {
      date: '2025-03-29',
      type: 'Partial',
      path: 'NW Africa, Europe, N Russia',
      maxDuration: null,
      saros: 149,
      visibility: { lat: [30, 80], lng: [-30, 60] }
    },
    {
      date: '2025-09-21',
      type: 'Partial',
      path: 'S Pacific, NZ, Antarctica',
      maxDuration: null,
      saros: 154,
      visibility: { lat: [-80, -20], lng: [100, -120] }
    },
    {
      date: '2026-02-17',
      type: 'Annular',
      path: 'Antarctica',
      maxDuration: '2m20s',
      saros: 121,
      visibility: { lat: [-80, -60], lng: [-60, 60] }
    },
    {
      date: '2026-08-12',
      type: 'Total',
      path: 'Arctic, Greenland, Iceland, Spain',
      maxDuration: '2m18s',
      saros: 126,
      visibility: { lat: [30, 90], lng: [-60, 30] }
    },
    {
      date: '2027-02-06',
      type: 'Annular',
      path: 'S America, Atlantic, Africa',
      maxDuration: '7m51s',
      saros: 131,
      visibility: { lat: [-50, 20], lng: [-80, 50] }
    },
    {
      date: '2027-08-02',
      type: 'Total',
      path: 'Morocco, Spain, Algeria, Tunisia, Egypt, Saudi Arabia',
      maxDuration: '6m23s',
      saros: 136,
      visibility: { lat: [10, 60], lng: [-20, 80] }
    },
    {
      date: '2028-01-26',
      type: 'Annular',
      path: 'S America, Atlantic',
      maxDuration: '10m27s',
      saros: 141,
      visibility: { lat: [-60, 10], lng: [-100, 0] }
    },
    {
      date: '2028-07-22',
      type: 'Total',
      path: 'Australia, New Zealand',
      maxDuration: '5m10s',
      saros: 146,
      visibility: { lat: [-50, 0], lng: [100, 180] }
    },
    {
      date: '2029-01-14',
      type: 'Partial',
      path: 'N America, C America',
      maxDuration: null,
      saros: 151,
      visibility: { lat: [10, 60], lng: [-140, -60] }
    },
    {
      date: '2029-06-12',
      type: 'Partial',
      path: 'Arctic, Scandinavia, Russia',
      maxDuration: null,
      saros: 118,
      visibility: { lat: [50, 90], lng: [-30, 150] }
    },
    {
      date: '2029-07-11',
      type: 'Partial',
      path: 'S America, Antarctica',
      maxDuration: null,
      saros: 156,
      visibility: { lat: [-80, -20], lng: [-120, -30] }
    },
    {
      date: '2029-12-05',
      type: 'Partial',
      path: 'Antarctica',
      maxDuration: null,
      saros: 123,
      visibility: { lat: [-80, -50], lng: [-150, 150] }
    },
    {
      date: '2030-06-01',
      type: 'Annular',
      path: 'Algeria, Tunisia, Greece, Turkey, Russia, Japan',
      maxDuration: '5m21s',
      saros: 128,
      visibility: { lat: [20, 70], lng: [-10, 160] }
    },
    {
      date: '2030-11-25',
      type: 'Total',
      path: 'Botswana, S Africa, Australia',
      maxDuration: '3m44s',
      saros: 133,
      visibility: { lat: [-50, 0], lng: [10, 160] }
    }
  ],
  lunar: [
    {
      date: '2024-09-18',
      type: 'Partial',
      visibility: 'Americas, Europe, Africa',
      maxMagnitude: 0.08
    },
    {
      date: '2025-03-14',
      type: 'Total',
      visibility: 'Americas, W Europe, W Africa',
      maxMagnitude: 1.18,
      totalDuration: '1h05m'
    },
    {
      date: '2025-09-07',
      type: 'Total',
      visibility: 'Europe, Africa, Asia, Australia',
      maxMagnitude: 1.36,
      totalDuration: '1h22m'
    },
    {
      date: '2026-03-03',
      type: 'Total',
      visibility: 'E Asia, Australia, Pacific, Americas',
      maxMagnitude: 1.15,
      totalDuration: '0h58m'
    },
    {
      date: '2026-08-28',
      type: 'Partial',
      visibility: 'E Europe, Asia, Australia, Pacific',
      maxMagnitude: 0.93
    },
    {
      date: '2028-01-12',
      type: 'Partial',
      visibility: 'Americas, Europe, Africa',
      maxMagnitude: 0.07
    },
    {
      date: '2028-07-06',
      type: 'Partial',
      visibility: 'Americas, Europe, Africa',
      maxMagnitude: 0.39
    },
    {
      date: '2028-12-31',
      type: 'Total',
      visibility: 'Europe, Africa, Asia, Australia',
      maxMagnitude: 1.24,
      totalDuration: '1h11m'
    },
    {
      date: '2029-06-26',
      type: 'Total',
      visibility: 'Americas, Europe, Africa',
      maxMagnitude: 1.84,
      totalDuration: '1h42m'
    },
    {
      date: '2029-12-20',
      type: 'Total',
      visibility: 'Americas, Europe, Africa, W Asia',
      maxMagnitude: 1.12,
      totalDuration: '0h53m'
    },
    {
      date: '2030-06-15',
      type: 'Partial',
      visibility: 'Europe, Asia, Africa, Australia',
      maxMagnitude: 0.50
    }
  ]
};

const EclipsePlanner = ({ latitude, longitude }) => {
  const { t } = useI18n();
  const [eclipseType, setEclipseType] = useState('solar');
  const [selectedEclipse, setSelectedEclipse] = useState(null);
  const [showPast, setShowPast] = useState(false);

  // Filter and sort eclipses
  const filteredEclipses = useMemo(() => {
    const now = new Date();
    const eclipses = ECLIPSES[eclipseType];
    
    return eclipses
      .map(eclipse => {
        const eclipseDate = new Date(eclipse.date);
        const isPast = eclipseDate < now;
        const daysUntil = Math.ceil((eclipseDate - now) / (1000 * 60 * 60 * 24));
        
        // Check if location is in visibility zone (rough approximation)
        let isVisible = false;
        if (eclipse.visibility) {
          if (eclipseType === 'solar') {
            const { lat, lng } = eclipse.visibility;
            isVisible = latitude >= lat[0] && latitude <= lat[1];
            // Longitude check is more complex due to wrapping
          } else {
            isVisible = eclipse.visibility.toLowerCase().includes('europe') ||
                       eclipse.visibility.toLowerCase().includes('africa');
          }
        }
        
        return { ...eclipse, isPast, daysUntil, isVisible };
      })
      .filter(eclipse => showPast || !eclipse.isPast)
      .sort((a, b) => new Date(a.date) - new Date(b.date));
  }, [eclipseType, latitude, showPast]);

  // Next eclipse
  const nextEclipse = filteredEclipses.find(e => !e.isPast);

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    });
  };

  const getTypeColor = (type) => {
    switch (type) {
      case 'Total': return 'bg-red-500/20 text-red-300 border-red-500/30';
      case 'Annular': return 'bg-amber-500/20 text-amber-300 border-amber-500/30';
      case 'Partial': return 'bg-blue-500/20 text-blue-300 border-blue-500/30';
      default: return 'bg-slate-500/20 text-slate-300';
    }
  };

  const getTypeLabel = (type) => {
    switch (type) {
      case 'Total': return t('eclipse.total');
      case 'Annular': return t('eclipse.annular');
      case 'Partial': return t('eclipse.partial');
      default: return type;
    }
  };

  const openTimeAndDate = (eclipse) => {
    const date = eclipse.date.replace(/-/g, '');
    const type = eclipseType === 'solar' ? 'solar' : 'lunar';
    window.open(`https://www.timeanddate.com/eclipse/${type}/${date}`, '_blank');
  };

  return (
    <div className="space-y-4">
      {/* Type Selector */}
      <div className="flex gap-2">
        <Button
          variant={eclipseType === 'solar' ? 'default' : 'outline'}
          size="sm"
          onClick={() => setEclipseType('solar')}
          className={eclipseType === 'solar' ? 'bg-amber-500 text-black' : 'border-white/10'}
        >
          <Sun className="w-4 h-4 mr-1" />
          {t('eclipse.solarEclipses')}
        </Button>
        <Button
          variant={eclipseType === 'lunar' ? 'default' : 'outline'}
          size="sm"
          onClick={() => setEclipseType('lunar')}
          className={eclipseType === 'lunar' ? 'bg-blue-500 text-white' : 'border-white/10'}
        >
          <Moon className="w-4 h-4 mr-1" />
          {t('eclipse.lunarEclipses')}
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setShowPast(!showPast)}
          className={showPast ? 'text-slate-400' : 'text-slate-600'}
        >
          {showPast ? t('eclipse.hidePast') : t('eclipse.showPast')}
        </Button>
      </div>

      {/* Next Eclipse Highlight */}
      {nextEclipse && (
        <Card className={`border ${
          nextEclipse.type === 'Total' 
            ? 'bg-gradient-to-r from-red-500/20 to-orange-500/20 border-red-500/30' 
            : 'bg-gradient-to-r from-amber-500/20 to-yellow-500/20 border-amber-500/30'
        }`}>
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-heading flex items-center gap-2">
              {eclipseType === 'solar' ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
              {eclipseType === 'solar' ? t('eclipse.nextSolarEclipse') : t('eclipse.nextLunarEclipse')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between">
              <div>
                <div className="flex items-center gap-2">
                  <Badge className={getTypeColor(nextEclipse.type)}>
                    {getTypeLabel(nextEclipse.type)}
                  </Badge>
                  <span className="text-lg font-bold text-white">{formatDate(nextEclipse.date)}</span>
                </div>
                <p className="text-sm text-slate-400 mt-1">
                  {eclipseType === 'solar' ? nextEclipse.path : nextEclipse.visibility}
                </p>
                {nextEclipse.maxDuration && (
                  <p className="text-xs text-slate-500 mt-1">{t('eclipse.maxDuration')} {nextEclipse.maxDuration}</p>
                )}
              </div>
              <div className="text-right">
                <div className="text-2xl font-bold text-amber-400">{nextEclipse.daysUntil}</div>
                <div className="text-xs text-slate-500">{t('eclipse.days')}</div>
              </div>
            </div>
            <Button
              variant="outline"
              size="sm"
              className="mt-3 w-full border-white/10"
              onClick={() => openTimeAndDate(nextEclipse)}
            >
              <ExternalLink className="w-4 h-4 mr-1" />
              {t('eclipse.viewDetailsOnTimeAndDate')}
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Eclipse List */}
      <Card className="bg-slate-900/50 border-white/10">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-heading flex items-center gap-2">
            <Calendar className="w-4 h-4 text-cyan-400" />
            {eclipseType === 'solar' ? t('eclipse.solar') : t('eclipse.lunar')} {t('eclipse.eclipseCalendar')}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-2 max-h-72 overflow-y-auto">
          {filteredEclipses.map((eclipse, index) => (
            <div
              key={index}
              className={`p-3 rounded-lg border transition-all cursor-pointer ${
                eclipse.isPast 
                  ? 'border-white/5 bg-slate-800/20 opacity-50' 
                  : selectedEclipse === index
                    ? 'border-amber-500/50 bg-amber-500/10'
                    : 'border-white/5 bg-slate-800/30 hover:bg-slate-800/50'
              }`}
              onClick={() => setSelectedEclipse(selectedEclipse === index ? null : index)}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="text-center min-w-[50px]">
                    <div className="text-sm font-bold text-white">
                      {new Date(eclipse.date).getDate()}
                    </div>
                    <div className="text-xs text-slate-500">
                      {new Date(eclipse.date).toLocaleString('default', { month: 'short' })}
                    </div>
                    <div className="text-xs text-slate-600">
                      {new Date(eclipse.date).getFullYear()}
                    </div>
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <Badge className={`text-xs ${getTypeColor(eclipse.type)}`}>
                        {getTypeLabel(eclipse.type)}
                      </Badge>
                      {!eclipse.isPast && eclipse.daysUntil <= 365 && (
                        <span className="text-xs text-amber-500">{eclipse.daysUntil}d</span>
                      )}
                    </div>
                    <p className="text-xs text-slate-400 mt-1 line-clamp-1">
                      {eclipseType === 'solar' ? eclipse.path : eclipse.visibility}
                    </p>
                  </div>
                </div>
                <ChevronRight className={`w-4 h-4 text-slate-500 transition-transform ${
                  selectedEclipse === index ? 'rotate-90' : ''
                }`} />
              </div>

              {/* Expanded Details */}
              {selectedEclipse === index && (
                <div className="mt-3 pt-3 border-t border-white/10 space-y-2">
                  {eclipseType === 'solar' ? (
                    <>
                      {eclipse.maxDuration && (
                        <div className="flex items-center gap-2 text-sm">
                          <Clock className="w-3 h-3 text-slate-500" />
                          <span className="text-slate-400">{t('eclipse.maxDuration')}</span>
                          <span className="text-white">{eclipse.maxDuration}</span>
                        </div>
                      )}
                      <div className="flex items-center gap-2 text-sm">
                        <MapPin className="w-3 h-3 text-slate-500" />
                        <span className="text-slate-400">{t('eclipse.path')}</span>
                        <span className="text-white">{eclipse.path}</span>
                      </div>
                      <div className="text-xs text-slate-500">
                        {t('eclipse.sarosCycle')} {eclipse.saros}
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="flex items-center gap-2 text-sm">
                        <Eye className="w-3 h-3 text-slate-500" />
                        <span className="text-slate-400">{t('eclipse.visibility')}</span>
                        <span className="text-white">{eclipse.visibility}</span>
                      </div>
                      <div className="flex items-center gap-2 text-sm">
                        <Moon className="w-3 h-3 text-slate-500" />
                        <span className="text-slate-400">{t('eclipse.magnitude')}</span>
                        <span className="text-white">{eclipse.maxMagnitude}</span>
                      </div>
                      {eclipse.totalDuration && (
                        <div className="flex items-center gap-2 text-sm">
                          <Clock className="w-3 h-3 text-slate-500" />
                          <span className="text-slate-400">{t('eclipse.totality')}</span>
                          <span className="text-white">{eclipse.totalDuration}</span>
                        </div>
                      )}
                    </>
                  )}
                  <Button
                    variant="outline"
                    size="sm"
                    className="w-full mt-2 border-white/10"
                    onClick={(e) => {
                      e.stopPropagation();
                      openTimeAndDate(eclipse);
                    }}
                  >
                    <ExternalLink className="w-3 h-3 mr-1" />
                    {t('eclipse.viewMapAndTimes')}
                  </Button>
                </div>
              )}
            </div>
          ))}
        </CardContent>
      </Card>

      {/* Safety Warning for Solar */}
      {eclipseType === 'solar' && (
        <Card className="bg-red-500/10 border-red-500/30">
          <CardContent className="p-3 flex items-start gap-2">
            <AlertTriangle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
            <div className="text-xs text-red-300">
              <strong>{t('eclipse.safetyWarningTitle')}</strong> {t('eclipse.safetyWarningText')}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Photography Tips */}
      <Card className="bg-slate-900/50 border-white/10">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-heading">
            📸 {eclipseType === 'solar' ? t('eclipse.solar') : t('eclipse.lunar')} {t('eclipse.photographyTips')}
          </CardTitle>
        </CardHeader>
        <CardContent className="text-xs text-slate-400 space-y-2">
          {eclipseType === 'solar' ? (
            <>
              <p>• <strong>{t('eclipse.tipFilterRequiredLabel')}</strong> - {t('eclipse.tipFilterRequiredText')}</p>
              <p>• <strong>{t('eclipse.focalLength')}</strong> {t('eclipse.tipFocalLengthSolarText')}</p>
              <p>• <strong>{t('eclipse.tipBracketingLabel')}</strong> {t('eclipse.tipBracketingText')}</p>
              <p>• <strong>{t('eclipse.totality')}</strong> {t('eclipse.tipTotalityCaptureText')}</p>
            </>
          ) : (
            <>
              <p>• <strong>{t('eclipse.tipNoFilterLabel')}</strong> - {t('eclipse.tipNoFilterText')}</p>
              <p>• <strong>{t('eclipse.focalLength')}</strong> {t('eclipse.tipFocalLengthLunarText')}</p>
              <p>• <strong>{t('eclipse.tipSettingsLabel')}</strong> {t('eclipse.tipSettingsText')}</p>
              <p>• <strong>{t('eclipse.totality')}</strong> {t('eclipse.tipTotalityIncreaseText')}</p>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default EclipsePlanner;
